import { useMemo, type MouseEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Skeleton, Tooltip, Typography } from '@mui/material';
import { parse } from 'date-fns';
import { useBookings, type BookingListItem } from '@/hooks/useBookings';
import { useTenantSettings } from '@/hooks/useTenantSettings';
import type { WorkshopOpeningHours } from '@/types';

/**
 * Selected-day booking timeline for the create-wizard time picker (#2118).
 * Existing work is shown as bars; a click on the track picks a 15-minute time
 * via the same `yyyy-MM-dd'T'HH:mm` `onChange` the slot chips use.
 */

const WEEKDAYS: (keyof WorkshopOpeningHours)[] = [
  'sunday',
  'monday',
  'tuesday',
  'wednesday',
  'thursday',
  'friday',
  'saturday',
];

const FALLBACK_OPEN_MINUTES = 8 * 60;
const FALLBACK_CLOSE_MINUTES = 17 * 60;
const PX_PER_HOUR = 56;
const MIN_BAR_PCT = 3;
const DEFAULT_BAR_MINUTES = 60;

export interface DayBookingsTimelineProps {
  /** Civil date `yyyy-MM-dd` of the selected day. */
  date: string;
  /** Wizard state: `yyyy-MM-dd'T'HH:mm` local wallclock, or `''`. */
  value: string;
  onChange: (value: string) => void;
  /** Workshop open `HH:mm` when already known (scheduling slots / week strip). */
  openTime?: string;
  /** Workshop close `HH:mm` when already known. */
  closeTime?: string;
  estimatedDurationHours?: number;
}

export function DayBookingsTimeline({
  date,
  value,
  onChange,
  openTime,
  closeTime,
  estimatedDurationHours,
}: DayBookingsTimelineProps) {
  const { t } = useTranslation('calendar');
  const { tenantSettings, businessHours } = useTenantSettings();
  const timeZone = safeTimeZone(tenantSettings?.timezone);

  const bounds = useMemo(() => paddedUtcBounds(date), [date]);
  const { data, isLoading } = useBookings({
    fromDate: bounds.from,
    toDate: bounds.to,
    sortBy: 'scheduledAt',
    sortOrder: 'asc',
    limit: 100,
  });

  const bookings = useMemo(
    () => (data?.pages ?? []).flatMap((page) => page.data),
    [data],
  );

  const dayBookings = useMemo(
    () =>
      bookings.flatMap((booking) => {
        if (booking.status === 'cancelled') return [];
        const wall = wallclockInZone(booking.scheduledAt, timeZone);
        if (!wall || wall.date !== date) return [];
        const durationHours = booking.estimatedDurationHours;
        const durationMinutes =
          durationHours != null && durationHours > 0
            ? Math.max(15, Math.round(durationHours * 60))
            : DEFAULT_BAR_MINUTES;
        return [{ booking, start: wall.minutes, end: wall.minutes + durationMinutes }];
      }),
    [bookings, date, timeZone],
  );

  const range = useMemo(
    () =>
      resolveRange({
        date,
        openTime,
        closeTime,
        businessHours,
        bookingStarts: dayBookings.map((item) => item.start),
        bookingEnds: dayBookings.map((item) => item.end),
      }),
    [date, openTime, closeTime, businessHours, dayBookings],
  );

  const selectedMinutes = useMemo(() => {
    const [valueDate, valueTime] = value.split('T');
    if (!valueDate || !valueTime || valueDate !== date) return null;
    return parseHhmm(valueTime);
  }, [value, date]);

  const selectedDurationMinutes =
    estimatedDurationHours !== undefined && estimatedDurationHours > 0
      ? Math.max(15, Math.round(estimatedDurationHours * 60))
      : 15;

  const hourMarks = useMemo(() => {
    const firstHour = Math.floor(range.start / 60);
    const lastHour = Math.ceil(range.end / 60);
    const marks: { hour: number; pct: number }[] = [];
    for (let hour = firstHour; hour <= lastHour; hour += 1) {
      marks.push({ hour, pct: toPct(hour * 60, range.start, range.end) });
    }
    return marks;
  }, [range.start, range.end]);

  const handleTrackClick = (event: MouseEvent<HTMLDivElement>) => {
    const rect = event.currentTarget.getBoundingClientRect();
    if (rect.width <= 0) return;
    const ratio = (event.clientX - rect.left) / rect.width;
    const raw = range.start + ratio * (range.end - range.start);
    const rounded = roundToQuarterHour(raw);
    const clamped = Math.min(Math.max(rounded, range.start), Math.max(range.start, range.end - 15));
    onChange(`${date}T${formatHhmm(clamped)}`);
  };

  const spanHours = Math.max((range.end - range.start) / 60, 1);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
      <Typography variant="caption" color="text.secondary">
        {t('dayTimeline.title')}
      </Typography>

      {isLoading ? (
        <Skeleton variant="rounded" height={88} />
      ) : (
        <>
          {dayBookings.length === 0 && (
            <Typography variant="body2" color="text.secondary">
              {t('dayTimeline.empty')}
            </Typography>
          )}
          <Box sx={{ overflowX: 'auto', width: '100%' }}>
            <Box sx={{ minWidth: spanHours * PX_PER_HOUR, width: '100%' }}>
              <Box sx={{ position: 'relative', height: 16, mb: 0.25 }}>
                {hourMarks.map((mark) => (
                  <Typography
                    key={mark.hour}
                    variant="caption"
                    color="text.secondary"
                    sx={{
                      position: 'absolute',
                      left: `${mark.pct}%`,
                      transform: mark.pct === 0 ? 'none' : mark.pct >= 100 ? 'translateX(-100%)' : 'translateX(-50%)',
                      fontVariantNumeric: 'tabular-nums',
                      lineHeight: 1.2,
                    }}
                  >
                    {String(mark.hour).padStart(2, '0')}
                  </Typography>
                ))}
              </Box>
              <Box
                data-testid="day-bookings-timeline-track"
                role="slider"
                tabIndex={0}
                aria-label={t('dayTimeline.selectTime')}
                aria-valuemin={range.start}
                aria-valuemax={range.end}
                aria-valuenow={selectedMinutes ?? range.start}
                onClick={handleTrackClick}
                sx={{
                  position: 'relative',
                  height: 48,
                  borderRadius: 1,
                  bgcolor: 'action.hover',
                  cursor: 'pointer',
                  overflow: 'hidden',
                }}
              >
                {selectedMinutes !== null && (
                  <Box
                    aria-label={t('dayTimeline.selected')}
                    sx={{
                      position: 'absolute',
                      top: 0,
                      bottom: 0,
                      left: `${toPct(selectedMinutes, range.start, range.end)}%`,
                      width: `${Math.max(toPct(selectedMinutes + selectedDurationMinutes, range.start, range.end) - toPct(selectedMinutes, range.start, range.end), MIN_BAR_PCT)}%`,
                      bgcolor: 'primary.main',
                      opacity: 0.24,
                      pointerEvents: 'none',
                    }}
                  />
                )}
                {dayBookings.map(({ booking, start, end }) => (
                  <BookingBar
                    key={booking.id}
                    booking={booking}
                    start={start}
                    end={end}
                    rangeStart={range.start}
                    rangeEnd={range.end}
                    label={t('dayTimeline.booking', {
                      time: formatHhmm(start),
                      vehicle: booking.vehicleRegNumber,
                      customer: booking.customerName,
                    })}
                  />
                ))}
              </Box>
            </Box>
          </Box>
        </>
      )}
    </Box>
  );
}

function BookingBar({
  booking,
  start,
  end,
  rangeStart,
  rangeEnd,
  label,
}: {
  booking: BookingListItem;
  start: number;
  end: number;
  rangeStart: number;
  rangeEnd: number;
  label: string;
}) {
  const left = toPct(start, rangeStart, rangeEnd);
  const right = toPct(end, rangeStart, rangeEnd);
  const caption = [booking.vehicleRegNumber, booking.customerName]
    .map((part) => part.trim())
    .filter((part) => part.length > 0)
    .join(' · ');

  return (
    <Tooltip title={label}>
      <Box
        aria-label={label}
        sx={{
          position: 'absolute',
          top: 6,
          bottom: 6,
          left: `${left}%`,
          width: `${Math.max(right - left, MIN_BAR_PCT)}%`,
          px: 0.5,
          display: 'flex',
          alignItems: 'center',
          borderRadius: 0.5,
          bgcolor: 'info.main',
          color: 'info.contrastText',
          pointerEvents: 'none',
          overflow: 'hidden',
        }}
      >
        <Typography variant="caption" noWrap sx={{ fontWeight: 600, lineHeight: 1.2 }}>
          {caption}
        </Typography>
      </Box>
    </Tooltip>
  );
}

function resolveRange(args: {
  date: string;
  openTime: string | undefined;
  closeTime: string | undefined;
  businessHours: WorkshopOpeningHours;
  bookingStarts: number[];
  bookingEnds: number[];
}): { start: number; end: number } {
  const propStart = args.openTime !== undefined ? parseHhmm(args.openTime) : null;
  const propEnd = args.closeTime !== undefined ? parseHhmm(args.closeTime) : null;
  const workshop = hoursForDate(args.date, args.businessHours);
  const start = propStart ?? (workshop ? parseHhmm(workshop.open) : null);
  const end = propEnd ?? (workshop ? parseHhmm(workshop.close) : null);
  if (start !== null && end !== null && end > start) {
    return { start, end };
  }

  if (args.bookingStarts.length > 0 && args.bookingEnds.length > 0) {
    const minStart = Math.min(...args.bookingStarts);
    const maxEnd = Math.max(...args.bookingEnds);
    const paddedStart = Math.floor((minStart - 30) / 60) * 60;
    const paddedEnd = Math.ceil((maxEnd + 30) / 60) * 60;
    if (paddedEnd > paddedStart) {
      return {
        start: Math.max(0, paddedStart),
        end: Math.min(24 * 60, paddedEnd),
      };
    }
  }

  return { start: FALLBACK_OPEN_MINUTES, end: FALLBACK_CLOSE_MINUTES };
}

function hoursForDate(
  date: string,
  businessHours: WorkshopOpeningHours,
): { open: string; close: string } | null {
  const parsed = parse(date, 'yyyy-MM-dd', new Date());
  if (Number.isNaN(parsed.getTime())) return null;
  const key = WEEKDAYS[parsed.getDay()];
  if (!key) return null;
  return businessHours[key];
}

function paddedUtcBounds(date: string): { from: string; to: string } {
  const start = new Date(`${date}T00:00:00.000Z`);
  start.setUTCDate(start.getUTCDate() - 1);
  const end = new Date(`${date}T23:59:59.999Z`);
  end.setUTCDate(end.getUTCDate() + 1);
  return { from: start.toISOString(), to: end.toISOString() };
}

function parseHhmm(value: string): number | null {
  const match = /^(\d{1,2}):(\d{2})$/.exec(value.trim());
  if (!match) return null;
  const hours = Number(match[1]);
  const minutes = Number(match[2]);
  if (hours > 23 || minutes > 59) return null;
  return hours * 60 + minutes;
}

function formatHhmm(totalMinutes: number): string {
  const wrapped = ((Math.round(totalMinutes) % (24 * 60)) + 24 * 60) % (24 * 60);
  const hours = Math.floor(wrapped / 60);
  const minutes = wrapped % 60;
  return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
}

function roundToQuarterHour(minutes: number): number {
  return Math.round(minutes / 15) * 15;
}

function toPct(minutes: number, start: number, end: number): number {
  const span = end - start;
  if (span <= 0) return 0;
  return Math.min(100, Math.max(0, ((minutes - start) / span) * 100));
}

function safeTimeZone(value: string | undefined): string | undefined {
  if (!value) return undefined;
  try {
    Intl.DateTimeFormat('en', { timeZone: value }).format(new Date());
    return value;
  } catch {
    return undefined;
  }
}

function wallclockInZone(
  iso: string,
  timeZone: string | undefined,
): { date: string; minutes: number } | null {
  const instant = new Date(iso);
  if (Number.isNaN(instant.getTime())) return null;
  const base: Intl.DateTimeFormatOptions = {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
  };
  let parts: Intl.DateTimeFormatPart[];
  try {
    parts = new Intl.DateTimeFormat('en-GB', {
      ...base,
      ...(timeZone !== undefined ? { timeZone } : {}),
    }).formatToParts(instant);
  } catch {
    parts = new Intl.DateTimeFormat('en-GB', base).formatToParts(instant);
  }
  const year = partValue(parts, 'year');
  const month = partValue(parts, 'month');
  const day = partValue(parts, 'day');
  const hour = Number(partValue(parts, 'hour'));
  const minute = Number(partValue(parts, 'minute'));
  if (!year || !month || !day || Number.isNaN(hour) || Number.isNaN(minute)) return null;
  return { date: `${year}-${month}-${day}`, minutes: hour * 60 + minute };
}

function partValue(parts: Intl.DateTimeFormatPart[], type: Intl.DateTimeFormatPartTypes): string {
  return parts.find((part) => part.type === type)?.value ?? '';
}
